import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';

import type { InstallmentProgressProps } from './types';

import { WALLET_FAILED_INSTALMENT_SUPPORT_PHONE } from '../../constants';

import styles from './styles.module.scss';

export function InstallmentProgress({
  progress,
  footer,
}: InstallmentProgressProps): React.ReactElement | null {
  const countsMatchSegments = Boolean(
    progress &&
      progress.segments.length === progress.totalSegmentCount &&
      progress.segments.length > 0,
  );

  const segmentRows = useMemo(() => {
    if (!progress || !countsMatchSegments) {
      return null;
    }
    return progress.segments.map((segment) => {
      const segmentClassName: string =
        segment.segmentStatus === 'paid'
          ? styles.segmentPaid
          : segment.segmentStatus === 'failed'
          ? styles.segmentFailed
          : styles.segmentScheduled;

      const isFailed: boolean = segment.segmentStatus === 'failed';

      const statusLineMessageId: string =
        segment.segmentStatus === 'paid'
          ? 'certificates.wallet.instalment.tooltip.statusPaidOn'
          : segment.segmentStatus === 'failed'
          ? 'certificates.wallet.instalment.tooltip.statusFailedOn'
          : 'certificates.wallet.instalment.tooltip.statusScheduledOn';

      const tooltipTitle: React.ReactElement = (
        <div className={styles.tooltipContent}>
          <p className={styles.tooltipHeadline}>
            <FormattedMessage
              id="certificates.wallet.instalment.tooltip.paymentAndAmount"
              values={{
                number: segment.installmentNumber,
                amount: segment.amountFormatted,
              }}
            />
          </p>
          <p className={styles.tooltipSubline}>
            <FormattedMessage id={statusLineMessageId} values={{ date: segment.dateFormatted }} />
          </p>
        </div>
      );

      return (
        <Tooltip
          key={segment.installmentNumber}
          placement="top"
          overlayClassName={
            isFailed ? styles.segmentTooltipOverlayFailed : styles.segmentTooltipOverlay
          }
          title={tooltipTitle}
        >
          <span
            className={`${styles.segment} ${segmentClassName}`}
            tabIndex={0}
            role="img"
            aria-label={`${segment.statusLabel}, ${segment.dateFormatted}`}
          />
        </Tooltip>
      );
    });
  }, [countsMatchSegments, progress]);

  const hasRenderableProgress = Boolean(countsMatchSegments && segmentRows);
  const hasFailedInstalment = useMemo(
    () => progress?.segments.some((segment) => segment.segmentStatus === 'failed') ?? false,
    [progress?.segments],
  );
  const supportPhoneLink = useMemo(
    () => (
      <a
        href={`tel:${WALLET_FAILED_INSTALMENT_SUPPORT_PHONE.replace(/\s/g, '')}`}
        className={styles.supportPhoneLink}
      >
        {WALLET_FAILED_INSTALMENT_SUPPORT_PHONE}
      </a>
    ),
    [],
  );

  if (!hasRenderableProgress && !footer) {
    return null;
  }

  return (
    <section className={styles.root}>
      {hasRenderableProgress && progress && (
        <>
          <h3 className={styles.title}>
            <FormattedMessage id="certificates.wallet.instalmentProgress.title" />
          </h3>

          <div className={styles.progressBlock}>
            <div className={styles.progressTrack} role="presentation">
              {segmentRows}
            </div>

            <div className={styles.amountRow}>
              <span className={styles.amountPair}>
                {progress.paidAmountFormatted} / {progress.totalAmountFormatted}
              </span>
              <span className={styles.countPair}>
                <FormattedMessage
                  id="certificates.wallet.instalmentProgress.count"
                  values={{
                    completed: progress.completedCount,
                    total: progress.totalSegmentCount,
                  }}
                />
              </span>
            </div>
          </div>

          <p className={styles.nextPayment}>
            <FormattedMessage id="certificates.wallet.instalmentProgress.nextPaymentLabel" />{' '}
            <span className={styles.nextPaymentDate}>
              {progress.nextPaymentDateFormatted || '\u2014'}
            </span>
          </p>

          <div className={styles.warning} role="status">
            <FormattedMessage id="certificates.wallet.instalmentProgress.warning" />
          </div>

          {hasFailedInstalment && (
            <div className={styles.failedPaymentBanner} role="status">
              <FormattedMessage
                id="certificates.wallet.instalmentProgress.failedPaymentCallSupport"
                values={{ phone: supportPhoneLink }}
              />
            </div>
          )}
        </>
      )}

      {footer && (
        <>
          {hasRenderableProgress && <div className={styles.footerDivider} role="presentation" />}
          <div className={styles.footerSlot}>{footer}</div>
        </>
      )}
    </section>
  );
}
